/**
 * Helper used to add/remove/clear custom css classes to an element.
 * Useful if classes may be removed later, CssClassHelper will clear only classes which were registered
 * 
 * * Example: 
 * For a given div: '<div class="w-100 h-100"/>'
 * Attach helper: const cssClassHelper = new CssClassHelper(div);
 * 
 * * Add classes:
 * cssClassHelper.add("fw-bold", "justify-content-center");
 * Result in: <div class="w-100 h-100 fw-bold justify-content-center"/>
 * 
 * * Remove classes:
 * cssClassHelper.remove("fw-bold");
 * Result in: <div class="w-100 h-100 justify-content-center"/>
 * /!\ Will only work for css classes which have been added with the helper
 *
 * * Clear classes:
 * cssClassHelper.clear();
 * Result in: <div class="w-100 h-100"/> 
 * /!\ Will only work for css classes which have been added with the helper
 * (w-100 & h-100 weren't registered in css class helper, theses classes are keep on div)
 */
export class CssClassHelper {

    /** Css classes added on element */
    public addedCssClasses: string[] = [];

    constructor(private _elt: HTMLElement) { }

    /** 
     * Add classes on element, stored in helper
     */
    public add(...cssClasses: string[]): void {
        for (const cssClass of cssClasses) {
            if (cssClass == null || cssClass === "") { continue; }
            if (this.addedCssClasses.includes(cssClass)) { continue; }
            this.addedCssClasses.push(cssClass);
            this._elt.classList.add(cssClass);
        }
    }

    /** 
     * Set classes on element
     */
    public set(...cssClasses: string[]): void {
        this.clear();
        this.add(...cssClasses);
    }

    /**
     * Remove classes on element, removed it in helper
     * ! cssClasses which have not been registered with "add" or "set" will not be removed
     */
    public remove(...cssClasses: string[]): void {
        for (const cssClass of cssClasses) {
            const classIndex = this.addedCssClasses.indexOf(cssClass);
            if (classIndex < 0) { continue; }
            this.addedCssClasses.splice(classIndex, 1);
            this._elt.classList.remove(cssClass);
        }
    }

    /**
     * Toggle classes on element
     * ! css classes on element which have not been registered with "add" or "set" will not be toggled
     */
    public toggle(...cssClasses: string[]): void {
        for (const cssClass of cssClasses) {
            if (this.addedCssClasses.includes(cssClass)) {// If class already in list, remove it
                this.remove(cssClass);
            } else {// Class isn't in list, add it
                this.add(cssClass);
            }
        }
    }

    /** 
     * Remove all classes added on element with helper 
     * ! css classes on element which have not been registered with "add" or "set" will not be removed
     */
    public clear(): void {
        this._elt.classList.remove(...this.addedCssClasses);
        this.addedCssClasses.length = 0;
    }
}